import * as React from "react";
import { useLocalStorage } from "react-use";
import { useSearchParams } from "react-router";

import { Loader } from "@/components/ui/loader";
import { SortDropdown, SortOption } from "@/components/sortDropdown";
import { useGetBusinessReportsOutcomeQuery } from "@/modules/snapshots/businessReports.api";
import type { SnapshotCollection, Subcollection } from "@/modules/snapshots/snapshotCollection.types";
import { Ordering } from "@/types/base";
import { Pagination } from "@/views/drive/pagination";
import type { ShiftCheckboxChangeMeta } from "@/components/ui/shift-checkbox";
import { getNextRangeSelection } from "@/helpers/rangeSelection";

import { SnapshotCollectionsOutcomesHeader } from "./header";
import { SnapshotCollectionsOutcomesListInput } from "./input";
import { SnapshotCollectionsOutcomesRow } from "./row";
import { SnapshotCollectionsOutcomesToolbar } from "./toolbar";
import { SnapshotCollectionsOutcomesWebsocketHandler } from "../websocket-handler";

interface Props {
  selectedCollection: SnapshotCollection;
  onFailedResultsAvailabilityChange?: (hasFailedResults: boolean) => void;
}

const DEFAULT_ORDERING: Ordering = { order_by: "status", descending: false };

export const SnapshotCollectionsOutcomesList: React.FC<Props> = ({
  selectedCollection,
  onFailedResultsAvailabilityChange,
}) => {
  const [page, setPage] = React.useState(1);
  const [query, setQuery] = React.useState("");
  const [ordering, setOrdering] = useLocalStorage<Ordering>(
    `snapshot-collections-outcomes-ordering-${selectedCollection.id}`,
    DEFAULT_ORDERING,
  );
  const currentOrdering = ordering ?? DEFAULT_ORDERING;

  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set());
  const lastSelectedIdRef = React.useRef<string | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const subcollectionId = searchParams.get("subcollection_id") ?? undefined;

  const { data, currentData, isLoading, isFetching, refetch, isError, error } = useGetBusinessReportsOutcomeQuery(
    {
      snapshot_collection_id: selectedCollection.id,
      subcollection_id: subcollectionId,
      offset: page - 1,
      query,
      ...currentOrdering,
    },
    { refetchOnMountOrArgChange: true },
  );

  React.useEffect(() => {
    if (
      error &&
      typeof error === "object" &&
      "status" in error &&
      (error as { status: number }).status === 422 &&
      currentOrdering.order_by !== DEFAULT_ORDERING.order_by
    ) {
      setOrdering(DEFAULT_ORDERING);
    }
  }, [error, currentOrdering.order_by, setOrdering]);

  React.useEffect(() => {
    onFailedResultsAvailabilityChange?.(Boolean(currentData?.has_failed_results));
  }, [currentData?.has_failed_results, onFailedResultsAvailabilityChange]);

  const onSortChange = ({ field, direction }: SortOption) => {
    setOrdering({ order_by: field, descending: direction === "desc" });
  };

  React.useEffect(() => {
    setPage(1);
    setSelectedIds(new Set());
    lastSelectedIdRef.current = null;
  }, [selectedCollection.id, subcollectionId]);

  const reset = () => {
    setPage(1);
    setQuery("");
  };

  const handleSubcollectionCreated = React.useCallback(
    (subcollection: Subcollection) => {
      setSelectedIds(new Set());
      lastSelectedIdRef.current = null;
      setQuery("");
      setPage(1);

      const next = new URLSearchParams(searchParams);
      next.set("subcollection_id", subcollection.id);
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  if (isError) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2">
        <p className="text-red-600">Error loading snapshot collection outcomes</p>
        <button
          className="rounded bg-red-600 px-4 py-2 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
          onClick={refetch}
        >
          Try Again
        </button>
      </div>
    );
  }

  if (isLoading || !data) {
    return <Loader />;
  }

  const { results: snapshotCollectionOutcomes = [], total } = data;
  const visibleIds = snapshotCollectionOutcomes.map(o => o.id);

  const toggleSelection = (id: string, checked: boolean, meta: ShiftCheckboxChangeMeta) => {
    setSelectedIds(
      prev =>
        new Set(
          getNextRangeSelection({
            orderedIds: visibleIds,
            selectedIds: Array.from(prev),
            anchorId: lastSelectedIdRef.current,
            targetId: id,
            targetChecked: checked,
            isRangeSelection: meta.shiftKey,
          }),
        ),
    );
    lastSelectedIdRef.current = id;
  };

  const setAllOnPageSelected = (ids: string[], allSelected: boolean) => {
    lastSelectedIdRef.current = null;
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allSelected) ids.forEach(id => next.delete(id));
      else ids.forEach(id => next.add(id));
      return next;
    });
  };

  return (
    <div className="mt-4">
      <SnapshotCollectionsOutcomesWebsocketHandler
        refetch={refetch}
        selectedCollection={selectedCollection}
        snapshotCollectionOutcomes={snapshotCollectionOutcomes}
      />
      <div className="lg:-mx-8">
        <div className="py-2 align-middle sm:px-6 lg:px-8">
          <SnapshotCollectionsOutcomesToolbar
            selectedIds={selectedIds}
            snapshotCollectionId={selectedCollection.id}
            subcollectionId={subcollectionId}
            onSubcollectionCreated={handleSubcollectionCreated}
          />
          <SnapshotCollectionsOutcomesListInput
            isFetching={isFetching}
            setQuery={setQuery}
            query={query}
            reset={reset}
          />
          <SortDropdown
            fields={[
              { key: "name", label: "Name" },
              { key: "status", label: "Status" },
              { key: "created_at", label: "Created Date" },
              { key: "verified", label: "Verified" },
            ]}
            currentSort={{
              field: currentOrdering.order_by,
              direction: currentOrdering.descending ? "desc" : "asc",
            }}
            onSortChange={onSortChange}
          />
          <div className="overflow-hidden ring-1 ring-[#d2cac0] sm:rounded-lg">
            <table className="min-w-full divide-y divide-gray-300">
              <SnapshotCollectionsOutcomesHeader
                ordering={currentOrdering}
                setOrdering={setOrdering}
                visibleIds={visibleIds}
                selectedIds={selectedIds}
                setAllOnPageSelected={setAllOnPageSelected}
              />
              <tbody className="divide-y divide-gray-200 bg-white">
                {snapshotCollectionOutcomes.map(businessReportOutcome => (
                  <SnapshotCollectionsOutcomesRow
                    businessReportOutcome={businessReportOutcome}
                    key={businessReportOutcome.id}
                    selectedIds={selectedIds}
                    toggleSelection={toggleSelection}
                  />
                ))}
                {!snapshotCollectionOutcomes.length && (
                  <tr>
                    <td colSpan={6} className="py-4 text-center text-sm text-gray-500">
                      No snapshots available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <Pagination currentPage={page} totalPages={Math.ceil(total / 10)} onPageChange={setPage} />
        </div>
      </div>
    </div>
  );
};
